import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../ui/buttons/button';
import {Form} from '../../ui/forms/form';
import {LinkStyle} from '../../ui/buttons/external-link';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '../../i18n/trans';
import {FormCheckbox} from '../../ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '../../core/settings/use-settings';
import {useContext} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';

export function RegisterPage() {
  const {
    branding,
    registration: {disable},
    social,
  } = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {email: searchParamsEmail},
  });
  const register = useRegister(form);

  if (disable) {
    return <Navigate to="/login" replace />;
  }

  let heading = <Trans message="Create a new account" />;
  if (isWorkspaceRegister) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to="/login">
            {parts}
          </Link>
        ),
      }}
      message=" "
    />
  );

  

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={async payload => {
          const isValid = await verify();
          if (isValid) {
            register.mutate(payload);
          }
        }}
      >
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          disabled={!!searchParamsEmail}
          label={<Trans message="Email" />}
          required
        />
        <FormTextField
          className="mb-32"
          name="password"
          type="password"
          label={<Trans message="Password" />}
          required
        />
        <div id="eye_area" className='toggle-password'>
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <g clip-path="url(#clip0_160_264)">
    <path d="M17.4532 7.06401C16.7761 5.95531 15.922 4.96496 14.9249 4.13226L17.0249 2.03226C17.1615 1.89081 17.2371 1.70136 17.2354 1.50471C17.2337 1.30807 17.1548 1.11996 17.0158 0.9809C16.8767 0.841844 16.6886 0.762968 16.492 0.761259C16.2953 0.75955 16.1059 0.835146 15.9644 0.971764L13.6807 3.25851C12.265 2.41763 10.6465 1.97936 8.99992 1.99101C4.35667 1.99101 1.71067 5.16951 0.546666 7.06401C0.187062 7.64564 -0.00341797 8.31594 -0.00341797 8.99976C-0.00341797 9.68358 0.187062 10.3539 0.546666 10.9355C1.22373 12.0442 2.07778 13.0346 3.07492 13.8673L0.974916 15.9673C0.903284 16.0364 0.846147 16.1192 0.80684 16.2107C0.767534 16.3022 0.746844 16.4006 0.745978 16.5002C0.745113 16.5998 0.764089 16.6986 0.8018 16.7907C0.839511 16.8829 0.895201 16.9666 0.96562 17.0371C1.03604 17.1075 1.11978 17.1632 1.21195 17.2009C1.30412 17.2386 1.40288 17.2576 1.50247 17.2567C1.60205 17.2558 1.70047 17.2351 1.79197 17.1958C1.88347 17.1565 1.96623 17.0994 2.03542 17.0278L4.32442 14.7388C5.73837 15.5795 7.35492 16.0185 8.99992 16.0085C13.6432 16.0085 16.2892 12.83 17.4532 10.9355C17.8128 10.3539 18.0033 9.68358 18.0033 8.99976C18.0033 8.31594 17.8128 7.64564 17.4532 7.06401ZM1.82467 10.1503C1.61102 9.80455 1.49785 9.40617 1.49785 8.99976C1.49785 8.59336 1.61102 8.19498 1.82467 7.84926C2.82517 6.22476 5.08642 3.49101 8.99992 3.49101C10.2451 3.48404 11.4728 3.78414 12.5744 4.36476L11.0647 5.87451C10.3446 5.39647 9.48135 5.18226 8.62134 5.26825C7.76133 5.35424 6.95757 5.73512 6.34642 6.34627C5.73527 6.95741 5.35439 7.76118 5.2684 8.62119C5.18241 9.48119 5.39662 10.3445 5.87467 11.0645L4.14217 12.797C3.22349 12.0543 2.43954 11.159 1.82467 10.1503ZM11.2499 8.99976C11.2499 9.5965 11.0129 10.1688 10.5909 10.5908C10.1689 11.0127 9.59665 11.2498 8.99992 11.2498C8.6658 11.2485 8.33631 11.1715 8.03617 11.0248L11.0249 8.03601C11.1717 8.33616 11.2486 8.66565 11.2499 8.99976ZM6.74992 8.99976C6.74992 8.40303 6.98697 7.83073 7.40893 7.40877C7.83088 6.98682 8.40318 6.74976 8.99992 6.74976C9.33403 6.75106 9.66352 6.82798 9.96367 6.97476L6.97492 9.96351C6.82813 9.66336 6.75121 9.33388 6.74992 8.99976ZM16.1752 10.1503C15.1747 11.7748 12.9134 14.5085 8.99992 14.5085C7.75471 14.5155 6.52699 14.2154 5.42542 13.6348L6.93517 12.125C7.65522 12.6031 8.51849 12.8173 9.37849 12.7313C10.2385 12.6453 11.0423 12.2644 11.6534 11.6533C12.2646 11.0421 12.6454 10.2383 12.7314 9.37834C12.8174 8.51833 12.6032 7.65506 12.1252 6.93501L13.8577 5.20251C14.7763 5.94522 15.5603 6.84055 16.1752 7.84926C16.3888 8.19498 16.502 8.59336 16.502 8.99976C16.502 9.40617 16.3888 9.80455 16.1752 10.1503Z" fill="#4D4D4D"/>
  </g>
  <defs>
    <clipPath id="clip0_160_264">
      <rect width="18" height="18" fill="white"/>
    </clipPath>
  </defs>
</svg>
        </div>
        <FormTextField
          className="mb-32"
          name="password_confirmation"
          type="password"
          label={<Trans message="Şifreni doğrula" />}
          required
        />
                <div id="eye_area3" className='toggle-password'>
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <g clip-path="url(#clip0_160_264)">
    <path d="M17.4532 7.06401C16.7761 5.95531 15.922 4.96496 14.9249 4.13226L17.0249 2.03226C17.1615 1.89081 17.2371 1.70136 17.2354 1.50471C17.2337 1.30807 17.1548 1.11996 17.0158 0.9809C16.8767 0.841844 16.6886 0.762968 16.492 0.761259C16.2953 0.75955 16.1059 0.835146 15.9644 0.971764L13.6807 3.25851C12.265 2.41763 10.6465 1.97936 8.99992 1.99101C4.35667 1.99101 1.71067 5.16951 0.546666 7.06401C0.187062 7.64564 -0.00341797 8.31594 -0.00341797 8.99976C-0.00341797 9.68358 0.187062 10.3539 0.546666 10.9355C1.22373 12.0442 2.07778 13.0346 3.07492 13.8673L0.974916 15.9673C0.903284 16.0364 0.846147 16.1192 0.80684 16.2107C0.767534 16.3022 0.746844 16.4006 0.745978 16.5002C0.745113 16.5998 0.764089 16.6986 0.8018 16.7907C0.839511 16.8829 0.895201 16.9666 0.96562 17.0371C1.03604 17.1075 1.11978 17.1632 1.21195 17.2009C1.30412 17.2386 1.40288 17.2576 1.50247 17.2567C1.60205 17.2558 1.70047 17.2351 1.79197 17.1958C1.88347 17.1565 1.96623 17.0994 2.03542 17.0278L4.32442 14.7388C5.73837 15.5795 7.35492 16.0185 8.99992 16.0085C13.6432 16.0085 16.2892 12.83 17.4532 10.9355C17.8128 10.3539 18.0033 9.68358 18.0033 8.99976C18.0033 8.31594 17.8128 7.64564 17.4532 7.06401ZM1.82467 10.1503C1.61102 9.80455 1.49785 9.40617 1.49785 8.99976C1.49785 8.59336 1.61102 8.19498 1.82467 7.84926C2.82517 6.22476 5.08642 3.49101 8.99992 3.49101C10.2451 3.48404 11.4728 3.78414 12.5744 4.36476L11.0647 5.87451C10.3446 5.39647 9.48135 5.18226 8.62134 5.26825C7.76133 5.35424 6.95757 5.73512 6.34642 6.34627C5.73527 6.95741 5.35439 7.76118 5.2684 8.62119C5.18241 9.48119 5.39662 10.3445 5.87467 11.0645L4.14217 12.797C3.22349 12.0543 2.43954 11.159 1.82467 10.1503ZM11.2499 8.99976C11.2499 9.5965 11.0129 10.1688 10.5909 10.5908C10.1689 11.0127 9.59665 11.2498 8.99992 11.2498C8.6658 11.2485 8.33631 11.1715 8.03617 11.0248L11.0249 8.03601C11.1717 8.33616 11.2486 8.66565 11.2499 8.99976ZM6.74992 8.99976C6.74992 8.40303 6.98697 7.83073 7.40893 7.40877C7.83088 6.98682 8.40318 6.74976 8.99992 6.74976C9.33403 6.75106 9.66352 6.82798 9.96367 6.97476L6.97492 9.96351C6.82813 9.66336 6.75121 9.33388 6.74992 8.99976ZM16.1752 10.1503C15.1747 11.7748 12.9134 14.5085 8.99992 14.5085C7.75471 14.5155 6.52699 14.2154 5.42542 13.6348L6.93517 12.125C7.65522 12.6031 8.51849 12.8173 9.37849 12.7313C10.2385 12.6453 11.0423 12.2644 11.6534 11.6533C12.2646 11.0421 12.6454 10.2383 12.7314 9.37834C12.8174 8.51833 12.6032 7.65506 12.1252 6.93501L13.8577 5.20251C14.7763 5.94522 15.5603 6.84055 16.1752 7.84926C16.3888 8.19498 16.502 8.59336 16.502 8.99976C16.502 9.40617 16.3888 9.80455 16.1752 10.1503Z" fill="#4D4D4D"/>
  </g>
  <defs>
    <clipPath id="clip0_160_264">
      <rect width="18" height="18" fill="white"/>
    </clipPath>
  </defs>
</svg>
        </div>
        {auth?.registerFields ? <auth.registerFields /> : null}
        <PolicyCheckboxes />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={register.isPending || isVerifying}
        >
          <Trans message="Hesap oluştur" />
        </Button>
        <SocialAuthSection
          dividerMessage={
            social.compact_buttons ? (
              <Trans message="Or sign up with" />
            ) : (
              <Trans message="OR" />
            )
          }
        />
      </Form>
      <div className="mt-36 text-sm"><Trans message="Hesabını oluşturduğunda Hizmet Şartlarını ve Gizlilik Politikasını kabul etmiş olursun." />   </div>
      <div id='register_label'><label> <Trans message="Hesabın var mı?" />  <a href="/login" id="register_button"><Trans message="Hemen giriş yap" /></a></label></div>
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {
    registration: {policies},
  } = useSettings();

  if (!policies) return null;

  return (
    <div className="mb-32">
      {policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          id='policy_info_text'
          required
        >
          <Trans
            //message="I accept the :name"
            message= "I agree to receive emails for updates and information from Linkcrafter."
            
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}
